import type {
  CaseSummary,
  LegalResearchDatabaseResult,
  LegalResearchRequest,
  LegalResearchResult,
  Recommendation,
  ResearchProgress,
  StatutoryMaterial,
} from 'src/services/legalResearchService';

export interface LegalResearch {
  id: string;
  type: 'legal_research';
  research_name: string;
  case_brief?: string;
  question?: string;
  answer?: string;
  jurisdiction: string;
  legal_field: string;
  summary: string;
  case_summaries: CaseSummary[];
  statutory_materials: StatutoryMaterial[];
  recommendations: Recommendation[];
  citations: string[];
  documents?: string[];
  status: 'COMPLETED' | 'IN_PROGRESS' | 'FAILED' | 'ENHANCING' | 'DELETING';
  dateCreated: string;
  client_id?: string;
  result_group_id: string;
  versionIndex?: number;
  totalVersions?: number;
  allVersions?: LegalResearch[];
  progress?: ResearchProgress;
}

export function parseProgress(raw: string | ResearchProgress | null | undefined): ResearchProgress | undefined {
  if (raw == null) return undefined;
  if (typeof raw === 'object' && raw !== null) return raw as ResearchProgress;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function extractQuestionFromRequest(request: LegalResearchRequest | undefined): string | undefined {
  if (!request) return undefined;
  const req = request as unknown as Record<string, unknown>;
  const meta = req?.metadata as Record<string, unknown> | undefined;
  const q = req?.question ?? meta?.initial_question;
  return typeof q === 'string' ? q : undefined;
}

function buildRequestOnlyResearch(
  row: LegalResearchDatabaseResult,
  statusOverride?: LegalResearch['status']
): LegalResearch {
  return {
    id: row.id,
    type: 'legal_research',
    research_name: row.request?.research_name || 'Untitled',
    case_brief: row.request?.case_brief || '',
    question: extractQuestionFromRequest(row.request),
    jurisdiction: row.request?.jurisdiction || '',
    legal_field: row.request?.legal_field || '',
    summary: '',
    case_summaries: [],
    statutory_materials: [],
    recommendations: [],
    citations: [],
    status: (statusOverride ?? row.status) as LegalResearch['status'],
    dateCreated: row.created_at,
    client_id: (row as unknown as Record<string, unknown>).client_id as string | undefined,
    result_group_id: row.result_group_id,
    progress: parseProgress(row.progress),
  };
}

function buildResultResearch(row: LegalResearchDatabaseResult, result: LegalResearchResult): LegalResearch {
  return {
    ...result,
    answer: result.answer,
    id: row.id,
    status: row.status as LegalResearch['status'],
    dateCreated: row.created_at,
    client_id: (row as unknown as Record<string, unknown>).client_id as string | undefined,
    result_group_id: row.result_group_id,
  } as LegalResearch;
}

function hasValidResultFields(result: LegalResearchResult): boolean {
  return (
    !!result.research_name &&
    !!result.jurisdiction &&
    !!result.legal_field &&
    Array.isArray(result.case_summaries) &&
    Array.isArray(result.statutory_materials) &&
    Array.isArray(result.recommendations) &&
    Array.isArray(result.citations)
  );
}

export function mapDatabaseRowToResearch(row: LegalResearchDatabaseResult): LegalResearch | null {
  if (row.status === 'IN_PROGRESS') {
    return buildRequestOnlyResearch(row);
  }

  if (row.status === 'ENHANCING' || row.status === 'DELETING') {
    if (row.result && row.result.case_summaries && Array.isArray(row.result.case_summaries)) {
      return buildResultResearch(row, row.result);
    }
    return buildRequestOnlyResearch(row);
  }

  if (row.status === 'FAILED') {
    return buildRequestOnlyResearch(row, 'FAILED');
  }

  if (row.status === 'COMPLETED' && row.result) {
    if (hasValidResultFields(row.result)) {
      const req = row.request as unknown as Record<string, unknown> | undefined;
      const meta = req?.metadata as Record<string, unknown> | undefined;
      const question = row.result.question ?? req?.question ?? meta?.initial_question;
      const docIds = req && Array.isArray(req.document_ids) ? (req.document_ids as string[]) : undefined;
      const documents = row.result.documents ?? docIds;
      return {
        ...row.result,
        answer: row.result.answer,
        question: typeof question === 'string' ? question : undefined,
        documents,
        id: row.id,
        status: row.status as LegalResearch['status'],
        dateCreated: row.created_at,
        client_id: (row as unknown as Record<string, unknown>).client_id as string | undefined,
        result_group_id: row.result_group_id,
      } as LegalResearch;
    }
    return buildRequestOnlyResearch(row, 'FAILED');
  }

  if (row.status === 'COMPLETED' && !row.result) {
    return buildRequestOnlyResearch(row, 'FAILED');
  }

  return null;
}

export interface GroupedResearchResult {
  latestResearches: LegalResearch[];
  versionMap: Map<string, LegalResearch[]>;
}

export function groupResearchesByVersion(mapped: LegalResearch[]): GroupedResearchResult {
  const grouped = new Map<string, LegalResearch[]>();
  mapped.forEach((research) => {
    const groupId = research.result_group_id;
    if (!grouped.has(groupId)) grouped.set(groupId, []);
    grouped.get(groupId)?.push(research);
  });

  const latestResearches: LegalResearch[] = [];
  const versionMap = new Map<string, LegalResearch[]>();

  grouped.forEach((versions, groupId) => {
    const sortedVersions = [...versions].sort(
      (a, b) => new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime()
    );
    versionMap.set(groupId, sortedVersions);

    const latestIndex = sortedVersions.length - 1;
    latestResearches.push({
      ...sortedVersions[latestIndex],
      versionIndex: latestIndex,
      totalVersions: sortedVersions.length,
      allVersions: sortedVersions,
    });
  });

  return { latestResearches, versionMap };
}

export function enrichWithSiblingData(research: LegalResearch, versions: LegalResearch[]): LegalResearch {
  const hasDocuments = (r: LegalResearch) => Array.isArray(r.documents) && r.documents.length > 0;
  const siblingDocuments = versions.find((v) => v.id !== research.id && hasDocuments(v))?.documents;
  return {
    ...research,
    ...(!hasDocuments(research) && siblingDocuments && { documents: siblingDocuments }),
  };
}

export function findResearchInVersionMap(
  id: string | undefined,
  versionMap: Map<string, LegalResearch[]>,
  fallbackResearches: LegalResearch[]
): LegalResearch | undefined {
  if (!id) return undefined;

  for (const [, versions] of Array.from(versionMap.entries())) {
    const foundVersion = versions.find((v) => v.id === id);
    if (foundVersion) {
      const versionIndex = versions.findIndex((v) => v.id === id);
      const enriched = enrichWithSiblingData(foundVersion, versions);
      return {
        ...enriched,
        versionIndex: versionIndex >= 0 ? versionIndex : 0,
        totalVersions: versions.length,
        allVersions: versions,
      };
    }
  }

  const directMatch = fallbackResearches.find((r) => r.id === id);
  if (directMatch) {
    const groupId = directMatch.result_group_id;
    const versions = versionMap.get(groupId) || [];
    if (versions.length === 0) return directMatch;

    const foundInVersions = versions.find((v) => v.id === id);
    if (foundInVersions) {
      const versionIndex = versions.findIndex((v) => v.id === id);
      const enriched = enrichWithSiblingData(foundInVersions, versions);
      return {
        ...enriched,
        versionIndex: versionIndex >= 0 ? versionIndex : 0,
        totalVersions: versions.length,
        allVersions: versions,
      };
    }
    return directMatch;
  }

  return undefined;
}

export function canDeleteResearchGroup(versions: Array<{ status: string }>): boolean {
  return versions.length > 0 && versions.every((v) => v.status === 'COMPLETED' || v.status === 'FAILED');
}
